import React, { useEffect, useState, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import 'moment/locale/th';

import { useTranslation } from 'react-i18next';

// leaflet
import L from 'leaflet';
import {
  Map, Marker, Popup, Tooltip, LayerGroup, LayersControl, TileLayer,
} from 'react-leaflet';
import Control from 'react-leaflet-control';
import 'leaflet/dist/leaflet.css';
import FullscreenControl from 'react-leaflet-fullscreen';
import { GestureHandling } from 'leaflet-gesture-handling';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import Button from '@material-ui/core/Button';

// icon
import { faCircle, faImage, faVideo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import LeafletFullScreenFix from '../../../utils/map/LeafletFullScreenFix';
import iconImage from '../../../components/marker/image/IconImage';
import {
  WaterLevelYellowIcon, WaterLevelRedIcon, WaterLevelBlueIcon,
  WaterLevelGreenIcon, WaterLevelBrownIcon, WaterLevelGreyIcon,
  TrendUpIcon, TrendDownIcon, TrendSteadyIcon,
} from '../../../assets/svg/Icon';
import { FaChartLine } from '../../../assets/font/Fontawesome';
import iconCircle from '../../../components/marker/circle/Circle';

// components
import mapConfig from '../../../components/map/Config';
import WaterLevelColor from '../../../components/color/waterlevel/WaterLevelColor';
import WaterLevelChart from '../chart/WaterLevelChart';
import WaterVolumeChart from '../chart/WaterVolumeChart';
import WaterGateChart from '../chart/WaterGateChart';
import WaterModal from '../../../components/modal/Modal';

// Layer
import LayerRiver from '../../../components/map/River';
import LayerProvince from '../../../components/map/Province';
import LayerBasin from '../../../components/map/Basin';
import WmsBoundary from '../../../components/map/WmsBoundary';
import WmsBasin from '../../../components/map/WmsBasin';

import CctvMedia from '../WLlists/cctvLists/CctvMedia';

// style
import styles from './style';
import { StyledIconButton } from '../../../components/button/Button';

const useStyles = makeStyles(styles);

const { Overlay } = LayersControl;

const createTrendIcon = (trend, color = '#1976d2') => {
  const iconMap = {
    up: <TrendUpIcon style={{ color, fontSize: '15px' }} />,
    down: <TrendDownIcon style={{ color, fontSize: '15px' }} />,
    steady: <TrendSteadyIcon style={{ color, fontSize: '15px' }} />,
  };

  const svgString = ReactDOMServer.renderToString(iconMap[trend] ?? iconMap.steady);

  return L.divIcon({
    className: '',
    html: `
      <div style="
        background: white;
        border: 2.5px solid ${color};
        border-radius: 50%;
        width: 22px; height: 22px;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">
        ${svgString}
      </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -13],
  });
};
export default function WaterMap(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const refMap = useRef(null);
  const refWaterLevel = useRef(null);
  const refCctv = useRef(null);
  const [modalData, setModalData] = useState({
    open: false,
    header: '',
    subheader: null,
    content: null,
  });
  const [currentPosition, setCurrentPosition] = useState();

  const {
    handleSetMap, currentMode, currentArea, type, dataWaterLevel, dataWaterVolume, dataCctv, dataWateGate,
  } = props;

  useEffect(() => {
    // set up map reference to list component
    handleSetMap(refMap.current.leafletElement);

    setModalData({
      open: false,
      header: '',
      subheader: null,
      content: null,
    });

    // set bounds
    if (currentArea.top_left && currentArea.bottom_right) {
      refMap.current.leafletElement.fitBounds(
        L.latLngBounds(currentArea.top_left, currentArea.bottom_right), { padding: [50, 50] },
      );
    }

    // add map gesture zoom
    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);
  }, [handleSetMap, currentMode, currentArea]);

  const bindMarker = (id) => (ref) => {
    const { markers } = props;

    if (ref) {
      markers[id] = ref.leafletElement;
    }
  };

  const handleWaterLevelChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟระดับน้ำ'),
      subheader: null,
      content: <WaterLevelChart id={data.station.id} provinceId={data.geocode.province_code} />,
    });
  };

  const handleWaterVolumeChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟปริมาณน้ำ'),
      subheader: null,
      content: <WaterVolumeChart id={data.station.id} provinceId={data.geocode.province_code} />,
    });
  };

  const handleWaterGateChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟระดับน้ำปตร./ฝาย'),
      subheader: null,
      content: <WaterGateChart id={data.station.id} provinceId={data.geocode.province_code} />,
    });
  };

  const handleCctvModal = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: 'CCTV',
      subheader: null,
      content: <CctvMedia data={data} />,
    });
  };

  // set marker icon based on criteria
  const setWaterLevelIcon = (val) => {
    let status = null;
    status = WaterLevelColor(val);
    return status.icon;
  };

  const setWaterLevelStatus = (wl) => {
    let wlStationIcon = null;
    if (wl === null) {
      wlStationIcon = (<WaterLevelGreyIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 100) {
      wlStationIcon = (<WaterLevelRedIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 70) {
      wlStationIcon = (<WaterLevelBlueIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 30) {
      wlStationIcon = (<WaterLevelGreenIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 10) {
      wlStationIcon = (<WaterLevelYellowIcon style={{ fontSize: '6rem' }} />);
    } else if (wl <= 10) {
      wlStationIcon = (<WaterLevelBrownIcon style={{ fontSize: '6rem' }} />);
    } else {
      wlStationIcon = (<WaterLevelGreyIcon style={{ fontSize: '6rem' }} />);
    }
    return wlStationIcon;
  };

  const formatWl = (value) => (typeof value === 'undefined' || value == null ? '-' : parseFloat(value).toFixed(2));

  const eachWaterLevelData = (data, i) => {
    if (data.station.tele_station_lat && data.station.tele_station_long
      && data.waterlevel_msl != null) {
      // marker position
      const position = [parseFloat(data.station.tele_station_lat),
      parseFloat(data.station.tele_station_long)];

      // status colors
      const statusColor = WaterLevelColor(data.storage_percent);
      const color = statusColor.color.statusColor;

      const current = parseFloat(data.waterlevel_msl);
      const previous = parseFloat(data.waterlevel_msl_previous);

      let trend = 'steady';
      if (current > previous) trend = 'up';
      else if (current < previous) trend = 'down';

      // marker icons
      const icon = setWaterLevelIcon(data.storage_percent);

      let locationText = '';
      if (data.geocode.tumbon_name.th !== undefined) {
        locationText = `${t('ต.')}${data.geocode.tumbon_name.th} `;
      }
      if (data.geocode.amphoe_name.th !== undefined) {
        locationText += `${t('อ.')}${data.geocode.amphoe_name.th} `;
      }
      if (data.geocode.province_name.th !== undefined) {
        locationText += `${t('จ.')}${data.geocode.province_name.th}`;
      }
      const minBank = data.station.min_bank;
      let compare = minBank - data.waterlevel_msl;
      let compareText = 'ต่ำ';
      if (data.waterlevel_msl > minBank) {
        compareText = 'สูง';
        compare = data.waterlevel_msl - minBank;
      }

      return (
        <Marker
          id={data.id}
          key={i}
          position={position}
          icon={icon}
          ref={bindMarker(data.id)}
        >
          <Popup autoPan>
            <TableContainer>
              <Table size="small">
                <TableHead />
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                        {data.station.tele_station_name.th}
                      </Box>
                      <Box fontFamily="Kanit" fontSize="12px">
                        {locationText}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Box>
                          {setWaterLevelStatus(parseFloat(data.storage_percent))}
                        </Box>
                        <Box>
                          <Box
                            className={classes.statusValue}
                            color={statusColor.color.statusColor}
                          >
                            {parseFloat(data.storage_percent)}
                            %
                          </Box>
                          <Box
                            p={1}
                            className={classes.statusText}
                            fontFamily="Kanit"
                            bgcolor={statusColor.color.statusColor}
                            color={statusColor.color.constrastColor}
                          >
                            {statusColor.color.label}
                          </Box>
                        </Box>
                      </Box>
                      <Box mb={2} fontSize="12px" fontFamily="Kanit">
                        {t('ปริมาณความจุลำน้ำ')}
                      </Box>
                      {/* <Box p={1} m={1} component="div" display="inline" style={{ fontSize: '1em' }}>
                        {t(data.diff_wl_bank_text)}
                      </Box>
                      <Box
                        p={1}
                        m={1}
                        component="div"
                        display="inline"
                        style={{
                          color: statusColor.color.statusColor,
                          fontWeight: 'bold',
                          fontSize: '1em',
                        }}
                      >
                        {data.diff_wl_bank}
                      </Box> */}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.waterlevel_msl)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับตลิ่ง')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(minBank)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t(`${compareText}กว่าตลิ่ง`)}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(compare)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับท้องน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.station.ground_level)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} colSpan={2}>
                      <Box color="text.disabled" fontSize="8px">
                        {t('ข้อมูล')}
                        :
                        {data.agency.agency_name.th}
                      </Box>
                      <Box color="text.disabled" fontSize="8px">
                        {t('ล่าสุด')}
                        {' '}
                        :
                        {data.waterlevel_datetime}
                        {' '}
                        น.
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">
                      <Button
                        title={t('แสดงกราฟ')}
                        style={{ padding: 0, minWidth: 18 }}
                        onClick={handleWaterLevelChart(data)}
                      >
                        <FaChartLine className={classes.rightIcon} />
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Popup>
          <Tooltip title={`สถานี${data.station.tele_station_name.th}`}>
            <span>
              <strong>
                {`สถานี${data.station.tele_station_name.th}`}
              </strong>
              <br />
              {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
              <br />
              {`${t('จ.')}${data.geocode.province_name.th}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }
    return false;
  };

  const eachWaterVolumeData = (data, i) => {
    let capacityPercentage = 0;
    if (data.discharge !== null && data.station.qmax !== null) {
      capacityPercentage = (data.discharge / data.station.qmax) * 100;
    }

    if (data.station.tele_station_lat && data.station.tele_station_long
      && data.station.hydro_id != null) {
      // marker position
      const position = [parseFloat(data.station.tele_station_lat),
      parseFloat(data.station.tele_station_long)];

      // status colors
      const statusColor = WaterLevelColor(capacityPercentage);
      const color = statusColor.color.statusColor;

      const current = parseFloat(data.waterlevel_msl);
      const previous = parseFloat(data.waterlevel_msl_previous);

      let trend = 'steady';
      if (current > previous) trend = 'up';
      else if (current < previous) trend = 'down';

      const icon = setWaterLevelIcon(capacityPercentage);
      
      let locationText = '';
      if (data.geocode.tumbon_name.th !== undefined) {
        locationText = `${t('ต.')}${data.geocode.tumbon_name.th} `;
      }
      if (data.geocode.amphoe_name.th !== undefined) {
        locationText += `${t('อ.')}${data.geocode.amphoe_name.th} `;
      }
      if (data.geocode.province_name.th !== undefined) {
        locationText += `${t('จ.')}${data.geocode.province_name.th}`;
      }
      const minBank = data.station.min_bank;
      const groundLevel = data.station.ground_level;
      let compare = minBank - data.waterlevel_msl;
      let compareText = 'ต่ำ';
      if (data.waterlevel_msl > minBank) {
        compareText = 'สูง';
        compare = data.waterlevel_msl - minBank;
      }


      return (
        <Marker
          id={data.id}
          key={i}
          position={position}
          icon={icon}
          ref={bindMarker(data.id)}
        >
          <Popup autoPan>
            <TableContainer>
              <Table size="small">
                <TableHead />
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                        {data.station.tele_station_name.th}
                      </Box>
                      <Box fontFamily="Kanit" fontSize="12px">
                        {locationText}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Box>
                          {setWaterLevelStatus(parseFloat(capacityPercentage).toFixed(2))}
                        </Box>
                        <Box>
                          <Box
                            className={classes.statusValue}
                            color={statusColor.color.statusColor}
                          >
                            {parseFloat(capacityPercentage).toFixed(2)}
                            %
                          </Box>
                          <Box
                            p={1}
                            className={classes.statusText}
                            fontFamily="Kanit"
                            bgcolor={statusColor.color.statusColor}
                            color={statusColor.color.constrastColor}
                          >
                            {statusColor.color.label}
                          </Box>
                        </Box>
                      </Box>
                      <Box mb={2} fontSize="12px" fontFamily="Kanit">
                        {t(`ปริมาณ${compareText}กว่าตลิ่ง`)}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ปริมาณน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.discharge)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.3/วิ.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ความจุลำน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.station.qmax)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.3/วิ.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.waterlevel_msl)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับตลิ่ง')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(minBank)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับท้องน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(groundLevel)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t(`${compareText}กว่าตลิ่ง`)}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(compare)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} colSpan={2}>
                      <Box color="text.disabled" fontSize="8px">
                        {t('ข้อมูล')}
                        :
                        {data.agency.agency_name.th}
                      </Box>
                      <Box color="text.disabled" fontSize="8px">
                        {t('ล่าสุด')}
                        {' '}
                        :
                        {data.waterlevel_datetime}
                        {' '}
                        น.
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">
                      <Button
                        title={t('แสดงกราฟ')}
                        style={{ padding: 0, minWidth: 18 }}
                        onClick={handleWaterVolumeChart(data)}
                      >
                        <FaChartLine className={classes.rightIcon} />
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Popup>
          <Tooltip title={`สถานี${data.station.tele_station_name.th}`}>
            <span>
              <strong>
                {`สถานี${data.station.tele_station_name.th}`}
              </strong>
              <br />
              {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
              <br />
              {`${t('จ.')}${data.geocode.province_name.th}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }
    return false;
  };

  const eachWaterGateLevelData = (data, i) => {
    if (data.station.tele_station_lat && data.station.tele_station_long != null) {
      const position = [parseFloat(data.station.tele_station_lat),
      parseFloat(data.station.tele_station_long)];
      let icon = iconCircle.gold;
      if (data.watergate_datetime_in < moment().format('YYYY-MM-DD')) {
        icon = iconCircle.grey;
      }
      let location = '';
      if (data.geocode.tumbon_name.th !== undefined) {
        location = `ต.${data.geocode.tumbon_name.th} `;
      }
      if (data.geocode.amphoe_name.th !== undefined) {
        location += `อ.${data.geocode.amphoe_name.th} `;
      }
      if (data.geocode.province_name.th !== undefined) {
        location += `จ.${data.geocode.province_name.th}`;
      }
      const station = (data.station.tele_station_name.th).split(' ');
      let stationList = data.station.tele_station_name.th;
      if (station.length > 1 && station[0].search('สถานีโทรมาตร') > -1) {
        stationList = station[1];
      }
      if (stationList === '') {
        stationList = data.station.tele_station_name.th;
      }

      return (
        <Marker
          id={data.station.id}
          key={i}
          position={position}
          icon={icon}
          ref={bindMarker(data.station.id)}
        >
          <Popup autoPan>
            <TableContainer>
              <Table size="small">
                <TableHead />
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                        {`${stationList}`}
                      </Box>
                      <Box fontFamily="Kanit" fontSize="12px">
                        {location}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับน้ำเหนือ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.watergate_in)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับน้ำท้าย')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.watergate_out)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} colSpan={2}>
                      <Box color="text.disabled" fontSize="8px">
                        {`${t('ข้อมูล')}: ${data.agency.agency_name.th}`}
                      </Box>
                      <Box color="text.disabled" fontSize="8px">
                        {t('ล่าสุด')}
                        {' '}
                        :
                        {data.watergate_datetime_in}
                        {' '}
                        {t('น.')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">
                      <Button
                        title={t('แสดงกราฟ')}
                        style={{ padding: 0, minWidth: 18 }}
                        onClick={handleWaterGateChart(data)}
                      >
                        <FaChartLine className={classes.rightIcon} />
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Popup>
          <Tooltip title={`${data.station.tele_station_name.th}`}>
            <span>
              <strong>
                {`${stationList}`}
              </strong>
              <br />
              {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
              <br />
              {`${t('จ.')}${data.geocode.province_name.th}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }
    return false;
  };

  const eachCctvData = (data, i) => {
    if (data.lat && data.long != null) {
      // marker position
      const position = [parseFloat(data.lat), parseFloat(data.long)];
      const address = `ต.${data.geocode.tumbon_name.th} อ.${data.geocode.amphoe_name.th} จ.${data.geocode.province_name.th}`;

      // marker icons
      let icon = iconImage.image;
      if (data.dam_id !== '') {
        icon = iconImage.dam;
      }
      if (data.media_type === 'vdo') {
        icon = iconImage.video;
      }
      let iconStyle = faImage;
      if (data.media_type === 'vdo') {
        iconStyle = faVideo;
      }

      return (
        <Marker
          id={data.id}
          key={i}
          position={position}
          icon={icon}
          ref={bindMarker(data.id)}
        >
          <Popup autoPan>
            <div>
              <TableContainer style={{ marginTop: 50 }}>
                <Table size="small">
                  <TableHead />
                  <TableBody>
                    <TableRow>
                      <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                        <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                          {data.title}
                        </Box>
                        <Box fontFamily="Kanit" fontSize="12px">
                          {address}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell padding="none" align="center" colSpan={3}>
                        <Box my={2} p={3} fontFamily="Kanit" fontSize="10px">
                          <StyledIconButton id="click_cctv_modal" onClick={handleCctvModal(data)}>
                            <FontAwesomeIcon icon={iconStyle} style={{ color: '#888' }} />
                          </StyledIconButton>
                          <br />
                          {t('ดูภาพ / วีดีโอ')}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell padding="none" align="center" colSpan={3} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Popup>
          <Tooltip title={data.title}>
            <span>
              <strong>
                {data.title}
              </strong>
              <br />
              {address}
            </span>
          </Tooltip>
        </Marker>
      );
    }

    return false;
  };

  const goToCurrentPosition = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;
      setCurrentPosition([latitude, longitude]);
      refMap.current.leafletElement.setView([latitude, longitude], 16, {
        animate: true,
      });
    },
    () => false,
    { enableHighAccuracy: true });
  };

  const Modal = React.memo((p) => (
    <WaterModal
      open={p.modalData.open}
      modalHeader={p.modalData.header}
      modalSubHeader={p.modalData.subheader}
      modalContent={p.modalData.content}
    />
  ));

  return (
    <div className={classes.root}>
      <Map
        id="map"
        center={currentArea.location ? currentArea.location : mapConfig.center}
        zoomSnap={0}
        zoomDelta={0.5}
        ref={refMap}
        style={{ height: 700, zIndex: 0 }}
        bounds={L.latLngBounds(currentArea.top_left, currentArea.bottom_right)}
        boundsOptions={{ padding: [50, 50] }}
        gestureHandling
      >

        {(type === 'watergate') && (
          <Control position="bottomleft">
            <div className="info">
              <Typography variant="body1">
                <FontAwesomeIcon icon={faCircle} color="#F6D300" />
                {' '}
                {t('ปตร./ฝาย')}
                <br />
                <FontAwesomeIcon icon={faCircle} color="#808080" />
                {' '}
                {t('ไม่มีข้อมูล')}
              </Typography>
            </div>
          </Control>
        )}

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          id="OpenStreetMap"
          attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>ESRI World Street Map</a> contributors"
        />
        {currentMode === 'province' && (
          <LayerProvince
            areaType={0}
            areaId={currentArea.code}
          />
        )}
        {currentMode === 'basin' && (
          <LayerBasin
            areaType={1}
            areaId={currentArea.code}
          />
        )}
        <LayersControl position="topright">
          {(type === 'waterlevel') && (
            <Overlay name={t('ระดับน้ำ')} checked="true">
              <LayerGroup ref={refWaterLevel}>
                {orderBy(dataWaterLevel, ['storage_percent'], ['asc']).map(eachWaterLevelData)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'waterVolume') && (
            <Overlay name={t('ปริมาณน้ำท่า')} checked="true">
              <LayerGroup ref={refWaterLevel}>
                {orderBy(dataWaterVolume, ['volumeCapacity'], ['asc']).map(eachWaterVolumeData)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'cctv') && (
            <Overlay name="CCTV" checked="true">
              <LayerGroup ref={refCctv}>
                {dataCctv
                  .map(eachCctvData)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'watergate') && (
            <Overlay name={t('ระดับน้ำปตร')} checked="true">
              <LayerGroup ref={refCctv}>
                {orderBy(dataWateGate, ['watergate_datetime_in'], ['desc']).map(eachWaterGateLevelData)}
              </LayerGroup>
            </Overlay>
          )}
          <Overlay name={t('เส้นแม่น้ำ')} checked>
            <LayerRiver />
          </Overlay>
          <Overlay name={t('ขอบเขตการปกครอง')}>
            <WmsBoundary />
          </Overlay>
          <Overlay name={t('ขอบเขตลุ่มน้ำ')}>
            <WmsBasin />
          </Overlay>
        </LayersControl>
        <FullscreenControl
          position="topleft"
          forcePseudoFullscreen
          content={LeafletFullScreenFix()}
        />
        <Control position="topleft">
          <div className="info">
            <Button
              onClick={goToCurrentPosition}
              style={{
                width: 20, minWidth: 20, height: 20, minHeight: 20,
              }}
            >
              <MyLocationIcon />
            </Button>
          </div>
        </Control>
        {currentPosition && (
          <Marker position={currentPosition} icon={L.icon({ iconUrl: 'marker-icon.png', popupAnchor: [12.5, 0] })}>
            <Popup>
              <div>
                <p>{t('ตำแหน่งปัจจุบัน')}</p>
              </div>
            </Popup>
          </Marker>
        )}
      </Map>

      <Modal modalData={modalData} />
    </div>
  );
}

WaterMap.propTypes = {
  dataWaterLevel: PropTypes.instanceOf(Array).isRequired,
  dataCctv: PropTypes.instanceOf(Array).isRequired,
  dataWateGate: PropTypes.instanceOf(Array).isRequired,
  handleSetMap: PropTypes.func.isRequired,
  markers: PropTypes.instanceOf(Array).isRequired,
  type: PropTypes.string.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
};
